<!--
 * Path: packages/frontend/src/features/browser/forms/StandardInput.vue
 * Task: Renders a standardized input component (text, markdown, select) utilizing the FormBlockWrapper.
 * Refactoring: Eliminated dependency on editorStore, mapping perfectly to pure composables.
-->
<template>
  <!-- Markdown relies on its own block-level wrapper natively, delegating everything -->
  <Markdown
    v-if="props.field.type === 'markdown' && activeEntity"
    v-model="fieldValueProxy"
    :label="props.field.name"
    :info="props.field.info"
    @click:info="showInfo"
  />

  <!-- Text and Selects get wrapped by the FormBlockWrapper -->
  <FormBlockWrapper
    v-else-if="activeEntity"
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    :title="props.field.name"
    :info="props.field.info"
    :can-edit="canEdit"
    :disable-edit="props.field.readonly"
    :is-saving="uiStore.loading.save"
    @save="saveChanges"
    @show-info="showInfo"
  >
    <template #read>
      <!-- Text -->
      <div v-if="props.field.type === 'text'" class="text-body-1 text-high-emphasis ms-1">
        {{ fieldValueProxy || '-' }}
      </div>
      <!-- Select -->
      <div v-else-if="props.field.type === 'select'" class="d-flex align-center ms-1 mt-1">
        <template v-if="displaySelectItem">
          <EntityIcon v-if="displaySelectItem.color || displaySelectItem.icon" :entity="{ icon: displaySelectItem.icon, color: displaySelectItem.color }" size="32" class="me-3" />
          <span class="text-body-1">{{ displaySelectItem.title }}</span>
        </template>
        <div v-else class="text-body-2 text-disabled">-</div>
      </div>
      <!-- Simple Select -->
      <div v-else-if="props.field.type === 'simple-select'" class="ms-1 mt-1">
        <v-chip v-if="fieldValueProxy || props.field.defaultValue" color="primary" variant="tonal" class="text-uppercase font-weight-bold">
          {{ fieldValueProxy || props.field.defaultValue }}
        </v-chip>
        <div v-else class="text-body-2 text-disabled">-</div>
      </div>
    </template>

    <template #edit>
      <v-text-field
        v-if="props.field.type === 'text'"
        v-model="fieldValueProxy"
        variant="outlined"
        density="compact"
        hide-details="auto"
        :error-messages="errorMessages"
        autofocus
        @keydown.enter="saveChanges"
        class="mb-0"
      ></v-text-field>

      <v-select
        v-else-if="props.field.type === 'select'"
        v-model="fieldValueProxy"
        :items="getSelectOptions(props.field.entitySource || '', props.field.id)"
        item-title="title"
        item-value="id"
        variant="outlined"
        density="compact"
        clearable
        hide-details="auto"
        :error-messages="errorMessages"
        class="mb-0"
      >
        <template v-slot:item="{ props: slotProps, item }">
          <v-list-item v-bind="slotProps" :title="item.raw.title">
            <template v-if="item.raw.color || item.raw.icon" v-slot:prepend>
              <EntityIcon :entity="{ icon: item.raw.icon, color: item.raw.color }" size="24" class="me-3" />
            </template>
          </v-list-item>
        </template>
        <template v-slot:selection="{ item }">
          <div class="d-flex align-center">
            <EntityIcon v-if="item.raw.color || item.raw.icon" :entity="{ icon: item.raw.icon, color: item.raw.color }" size="24" class="me-3" />
            <span>{{ item.raw.title }}</span>
          </div>
        </template>
      </v-select>

      <v-select
        v-else-if="props.field.type === 'simple-select'"
        v-model="fieldValueProxy"
        :items="props.field.options"
        variant="outlined"
        density="compact"
        clearable
        hide-details="auto"
        :error-messages="errorMessages"
        class="mb-0"
      ></v-select>
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { useSelectOptions } from '@/core/composables/useSelectOptions';
import { usePermissions } from '@/core/composables/usePermissions';
import { FormContextKey } from '@/core/keys';
import type { Entity } from '@/core/types/editor';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';
import Markdown from '@/core/components/forms/markdown/Markdown.vue';
import EntityIcon from '@/core/components/common/EntityIcon.vue';

export interface FieldProps {
  id: string;
  type: string;
  name: string;
  info?: string;
  readonly?: boolean;
  entitySource?: string;
  defaultValue?: string;
  options?: any[];
}

const props = defineProps<{ field: FieldProps; errorMessages?: string | string[] }>();

const emit = defineEmits<{
  (e: 'show-info', payload: { title: string; text?: string }): void;
}>();

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const { getSelectOptions } = useSelectOptions();
const formContext = inject(FormContextKey, null);

const activeEntity = computed<Entity | null>(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);
const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value && !props.field.readonly);

// --- Block-Level Edit Logic ---
const handleToggleEdit = (newVal: boolean) => {
  if (!newVal) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || false);
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  await saveEntity(formContext?.isSecondary || false);
  isEditing.value = false;
};

// Clean proxy for reading and writing to the globally injected entity state.
const fieldValueProxy = computed({
  get: () => {
    if (!activeEntity.value || !props.field.id) return '';
    return activeEntity.value[props.field.id];
  },
  set: (newValue: any) => {
    if (activeEntity.value && props.field.id) {
      activeEntity.value[props.field.id] = newValue;
    }
  }
});

const displaySelectItem = computed(() => {
  if (props.field.type !== 'select') return null;
  const items = getSelectOptions(props.field.entitySource || '', props.field.id);
  const val = fieldValueProxy.value;
  return items.find((item: any) => item.id === val) || null;
});

const showInfo = (): void => {
  emit('show-info', { title: props.field.name, text: props.field.info });
};
</script>
